import React, { Component, ErrorInfo, ReactNode } from 'react';
import { captureException, addBreadcrumb } from '@/lib/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  eventId?: string;
  errorCount?: number;
}

export class ErrorBoundary extends Component<Props, State> {
  private errorTimestamps: number[] = [];
  private readonly MAX_ERRORS_PER_MINUTE = 5;
  
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, errorCount: 0 };
  }

  static getDerivedStateFromError(error: Error): State {
    // Only show error UI for actual errors, not chunk loading issues
    if (error.message?.includes('Loading chunk') || 
        error.message?.includes('ChunkLoadError') ||
        error.message?.includes('Loading CSS chunk') ||
        error.message?.includes('Loading module') ||
        error.message?.includes('Importing a module script failed') ||
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError')) {
      // Silently ignore - the lazyWithRetry will handle it
      return { hasError: false, errorCount: 0 };
    }
    
    console.error('[ErrorBoundary] Caught error:', error);
    
    // Update state so the next render will show the fallback UI
    return { hasError: true, error, errorCount: 0 };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Check for error loop (too many errors in short time)
    const now = Date.now();
    this.errorTimestamps = this.errorTimestamps.filter(t => now - t < 60000); // Keep only last minute
    this.errorTimestamps.push(now);
    
    if (this.errorTimestamps.length > this.MAX_ERRORS_PER_MINUTE) {
      console.error('[ErrorBoundary] Too many errors detected! Stopping error loop.');
      // Force show error UI to stop the loop
      this.setState({ 
        hasError: true, 
        error: new Error('Too many errors detected. Please refresh the page.'),
        errorCount: this.errorTimestamps.length
      });
      return;
    }
    
    // Only process actual errors, not chunk loading issues
    if (error.message?.includes('Loading chunk') || 
        error.message?.includes('ChunkLoadError') ||
        error.message?.includes('Loading CSS chunk') ||
        error.message?.includes('Loading module') ||
        error.message?.includes('Importing a module script failed') ||
        error.message?.includes('Failed to fetch dynamically imported module') ||
        error.message?.includes('Failed to fetch') ||
        error.message?.includes('NetworkError')) {
      // Silently ignore - the lazyWithRetry will handle it
      return;
    }
    
    // Log the error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // 🔍 Adicionar breadcrumb antes de capturar
    addBreadcrumb({
      category: 'error-boundary',
      message: 'React Error Boundary caught an error',
      level: 'error',
      data: {
        componentStack: errorInfo.componentStack,
      },
    });
    
    // 🔍 Capturar exceção no Sentry
    const eventId = captureException(error, {
      contexts: {
        react: {
          componentStack: errorInfo.componentStack,
        },
      },
      tags: {
        errorBoundary: 'true',
      },
    });
    
    this.setState({
      error,
      errorInfo,
      eventId,
      errorCount: this.errorTimestamps.length
    });
  }

  render() {
    if (this.state.hasError) {
      // You can render any custom fallback UI
      return this.props.fallback || (
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="text-center space-y-4 max-w-md mx-auto p-6">
            <div className="text-6xl">⚠️</div>
            <h1 className="text-2xl font-bold text-foreground">
              Oops! Algo deu errado
            </h1>
            <p className="text-muted-foreground">
              {this.state.errorCount && this.state.errorCount > this.MAX_ERRORS_PER_MINUTE 
                ? 'Detectamos múltiplos erros. Por favor, recarregue a página.'
                : 'Ocorreu um erro inesperado. Por favor, recarregue a página ou tente novamente mais tarde.'}
            </p>
            <div className="space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors"
              >
                Recarregar Página
              </button>
              <button
                onClick={() => window.history.back()}
                className="px-4 py-2 bg-secondary text-secondary-foreground rounded-md hover:bg-secondary/90 transition-colors ml-2"
              >
                Voltar
              </button>
            </div>
            {process.env.NODE_ENV === 'development' && this.state.error && (
              <details className="mt-4 text-left">
                <summary className="cursor-pointer text-sm text-muted-foreground">
                  Detalhes do erro (desenvolvimento)
                </summary>
                <pre className="mt-2 text-xs bg-muted p-2 rounded overflow-auto">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                  {this.state.errorCount && `\n\nErros detectados: ${this.state.errorCount}`}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}